/* eslint-disable max-lines */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import lineItemStatusEnum from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import { filter, find, includes, isEmpty, map, some } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getPartners } from 'store/thunks/partnerThunks';

import { PrintLabelButton, PrintLabelProvider } from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';

import HistoryPanel from './components/HistoryPanel';
import JsonViewDialog from './components/JsonViewDialog';
import LineItemsTable from './components/LineItemsTable';
import OrderActionsMenu from './components/OrderActionsMenu';
import OrderDetailsPanel from './components/OrderDetailsPanel';
import OrderHeader from './components/OrderHeader';
import PatientPanel from './components/PatientPanel';
import PatientCurrentOrdersDropdown from './components/PatientCurrentOrdersDropdown';
import useAddNote from './hooks/useAddNote';
import useOrderData from './hooks/useOrderData';
import useOrderSocketRefresh from './hooks/useOrderSocketRefresh';
import useTimelineItems from './hooks/useTimelineItems';

// Minimum height of the line items panel so the table stays usable on short screens; the left
// column scrolls instead of squeezing it.
const LINE_ITEMS_MIN_HEIGHT = 360;

// AppLayout's root container reserves space below <main> that this full-height view doesn't need,
// so size the view against the viewport (minus the app bar spacer, which follows the toolbar
// mixin's breakpoints) to use the whole screen.
const viewportHeightSx = (theme) =>
  Object.entries(theme.mixins.toolbar).reduce(
    (sx, [key, value]) =>
      key === 'minHeight'
        ? { ...sx, height: `calc(100vh - ${value}px)` }
        : { ...sx, [key]: { height: `calc(100vh - ${value.minHeight}px)` } },
    {}
  );

const OrderView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderId } = useParams();

  const onClose = () => navigate(-1);

  const hasPartners = useSelector(({ partner, auth }) => !!partner[auth.currentTenantId]?.length);

  useEffect(() => {
    if (!hasPartners) dispatch(getPartners());
  }, [dispatch, hasPartners]);

  useEffect(() => {
    if (orderId) {
      document.title = `SmartOS - PerfectRx ${orderId}`;
    }
    return () => {
      document.title = 'SmartOS - PerfectRx';
    };
  }, [orderId]);

  const [historyEnabled, setHistoryEnabled] = useState(false);

  const {
    order,
    patient,
    events,
    requestEvents,
    comments,
    loading,
    reloading,
    enriching,
    historyLoading,
    notesLoading,
    notFound,
    orderAddress,
    deliveryInstructions,
    initialFulfillmentAddress,
    patientExternalId,
    timelineBottomRef,
    reloadComments,
    reloadHistory,
    reloadOrderAddress,
    reloadOrder,
  } = useOrderData({ orderId, historyEnabled });

  // Another user changed this order: pick up the new line item state and, when it's open, history.
  const { refreshPending } = useOrderSocketRefresh({
    order,
    reloading,
    onRefresh: () => Promise.all([reloadOrder(), reloadHistory()]),
  });

  // Actions are locked from the moment another user's change is known until the reload lands:
  // anything clicked in between would act on what the screen shows, which is already stale.
  const actionsLocked = reloading || refreshPending;

  const fulfillingPharmacyName = useSelector(
    ({ auth }) =>
      find(auth.fulfillingPharmacies, {
        fulfillingPharmacyId: order?.fulfillingPharmacyId,
      })?.fulfillingPharmacyName
  );

  const fulfillingPharmacyDisplay = useSelector(({ pharmacy, auth }) => {
    const pharmacies = pharmacy[auth.currentTenantId];
    const facility = find(
      pharmacies,
      (p) => p?.fulfillingPharmacyId === order?.fulfillingPharmacyId
    );
    if (facility?.name) {
      return facility.state ? `${facility.name} - ${facility.state}` : facility.name;
    }
    return fulfillingPharmacyName || null;
  });

  const hasAccess = useRoles();

  const isLineItemsHasLabelCreated = some(order?.lineItems, { isLabelCreated: true });

  const allActiveLineItemIds = map(
    filter(
      order?.lineItems ?? [],
      ({ taggedForReview, lineItemStatus, isLabelCreated, processingState }) =>
        order?.orderStatus !== orderEnum.CANCELED &&
        order?.orderStatus !== orderEnum.FILL_LATER &&
        lineItemStatus !== lineItemStatusEnum.RE_PROCESS &&
        lineItemStatus !== lineItemStatusEnum.CANCELED &&
        !includes(['CANCELED', 'FILL_LATER'], processingState) &&
        !taggedForReview &&
        !!isLabelCreated
    ),
    'lineItemId'
  );

  const [jsonToView, setJsonToView] = useState(null);

  const {
    newNoteText,
    setNewNoteText,
    savingNote,
    handleAddNote,
    isShippingNote,
    setIsShippingNote,
  } = useAddNote({
    order,
    reloadComments,
    timelineBottomRef,
  });

  const timelineItems = useTimelineItems({ events, requestEvents, comments, order, patient });

  return (
    <PrintLabelProvider initialValue={allActiveLineItemIds} lineItems={order?.lineItems ?? []}>
      {!isEmpty(jsonToView) && (
        <JsonViewDialog
          open={!isEmpty(jsonToView)}
          jsonData={jsonToView}
          closeModel={() => setJsonToView(null)}
        />
      )}
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          ...viewportHeightSx(theme),
          // <main> is a flex column sized by AppLayout; without this the view shrinks back to it.
          flexShrink: 0,
          overflow: 'hidden',
          ml: -1,
        })}
      >
        {/* ── Top Header bar ── */}
        {order && <OrderHeader order={order} fulfillingPharmacyName={fulfillingPharmacyDisplay} />}
        {!order && (
          <Grid
            container
            sx={{ px: 3, py: 1, bgcolor: 'primary.light' }}
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography variant='h6' color='white'>
              Order
            </Typography>
          </Grid>
        )}

        <Box sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {notFound && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant='h6' color='text.secondary'>
                Order not found
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                No order with ID &ldquo;{orderId}&rdquo; was found.
              </Typography>
              <Button variant='contained' onClick={onClose}>
                Go Back
              </Button>
            </Box>
          )}
          {!notFound && (loading || !order) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          )}
          {!notFound && !loading && order && (
            <Grid container sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
              {/* ── Left: details + line items ── */}
              <Grid
                item
                xs={9}
                sx={{
                  borderRight: '1px solid #e0e0e0',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                }}
              >
                {/* Breadcrumb + Actions row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    pt: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <Breadcrumbs aria-label='order breadcrumb'>
                    {order.patientName && (
                      <Typography
                        onClick={() => order.mpi && navigate(`/patients/${order.mpi}`)}
                        sx={{
                          fontWeight: 600,
                          fontSize: '1.25rem',
                          color: 'primary.main',
                          cursor: order.mpi ? 'pointer' : 'default',
                          '&:hover': order.mpi ? { textDecoration: 'underline' } : {},
                        }}
                      >
                        {order.patientName}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography fontWeight={600} fontSize='1.25rem'>
                        {order.orderId}
                      </Typography>
                      <Tooltip title={`Copy order ID: ${order.orderId}`}>
                        <IconButton
                          size='small'
                          onClick={() => navigator.clipboard.writeText(order.orderId)}
                          sx={{ p: 0.25, color: 'primary.main' }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {order.mpi && (
                        <PatientCurrentOrdersDropdown mpi={order.mpi} currentOrderId={orderId} />
                      )}
                    </Box>
                  </Breadcrumbs>
                  {actionsLocked && (
                    <Tooltip
                      title={
                        refreshPending
                          ? 'This order was updated by another user. Refreshing…'
                          : 'Refreshing…'
                      }
                    >
                      <CircularProgress size={18} thickness={4} sx={{ ml: 1, flexShrink: 0 }} />
                    </Tooltip>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OrderActionsMenu
                      order={order}
                      patient={patient}
                      orderAddress={orderAddress}
                      deliveryInstructions={deliveryInstructions}
                      events={events}
                      onActionComplete={() => {
                        reloadOrder();
                        reloadComments();
                        reloadHistory();
                      }}
                      onAddressUpdate={reloadOrderAddress}
                      enriching={enriching}
                      reloading={actionsLocked}
                    />
                  </Box>
                </Box>

                {/* Top row: Order Details + Patient */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    p: 2.5,
                    gap: 2,
                    flexShrink: 0,
                    minHeight: 275,
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <OrderDetailsPanel
                    order={order}
                    initialFulfillmentAddress={initialFulfillmentAddress}
                    orderAddress={orderAddress}
                  />
                  <Divider orientation='vertical' flexItem />
                  <PatientPanel
                    order={order}
                    patient={patient}
                    patientExternalId={patientExternalId}
                  />
                </Box>

                {/* Line Items */}
                <Paper
                  elevation={1}
                  sx={{
                    flex: 1,
                    minHeight: LINE_ITEMS_MIN_HEIGHT,
                    overflow: 'hidden',
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='subtitle2' fontWeight='bold'>
                        Items
                      </Typography>
                      {order.lineItems?.length > 0 && (
                        <Chip label={order.lineItems.length} size='small' color='info' />
                      )}
                    </Box>
                    {/* POM-6221: a VOID order is parked until its fill date. Nothing on it may be
                        printed, so the bar drops the button rather than offering one that would
                        have nothing selectable behind it. */}
                    {hasAccess.generateBulkLabels &&
                      isLineItemsHasLabelCreated &&
                      order.orderStatus !== orderEnum.TRANSFERRED &&
                      order.orderStatus !== orderEnum.FILL_LATER && (
                        <PrintLabelButton onSuccess={reloadOrder} disabled={actionsLocked} />
                      )}
                  </Box>
                  <Box sx={{ overflow: 'auto', flex: 1 }}>
                    <LineItemsTable
                      order={order}
                      onFillComplete={reloadOrder}
                      enriching={enriching}
                      actionsDisabled={actionsLocked}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* ── Right: History & Notes ── */}
              <Grid
                item
                xs={3}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <HistoryPanel
                  timelineItems={timelineItems}
                  historyLoading={historyLoading}
                  notesLoading={notesLoading}
                  onViewJson={setJsonToView}
                  timelineBottomRef={timelineBottomRef}
                  newNoteText={newNoteText}
                  setNewNoteText={setNewNoteText}
                  savingNote={savingNote}
                  handleAddNote={handleAddNote}
                  isShippingNote={isShippingNote}
                  setIsShippingNote={setIsShippingNote}
                  historyEnabled={historyEnabled}
                  onHistoryFirstActivated={() => setHistoryEnabled(true)}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </PrintLabelProvider>
  );
};

export default OrderView;
